import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TagIcon from "@mui/icons-material/Tag";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import { EditButton } from "@refinedev/mui";
import ShareColumn from "components/table/ShareColumn";
import { useConfig } from "provider/ConfigProvider";
import { Event } from "types/event";
import {
  getPublicEventUrl,
  isEventPublished,
  isEventScheduled,
} from "utils/eventUtils";
import { EventMenu } from "./EventMenu";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import { useBranchContext } from "../../provider/BranchProvider";
import ApartmentIcon from "@mui/icons-material/Apartment";
import React from "react";

import ShowMenuItem from "../../components/table/ShowMenuItem";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { QrCodeMenu } from "./QrCodeMenu";

export default function EventCard(props: { event: Event }) {
  const { event } = props;
  const highlightImage = event?.images?.find((i) => i.highlight);
  const { staffAppUrl, landingPageUrl } = useConfig();
  const { branch, branches } = useBranchContext();

  return (
    <Card>
      <Box position={"relative"}>
        {event.images && event.images.length > 0 ? (
          <CardMedia
            component="img"
            alt="Event Banner"
            height="200px"
            image={highlightImage ? highlightImage.url : event.images[0].url}
            sx={{ zIndex: 0 }}
          />
        ) : (
          <Skeleton variant="rectangular" height={200} animation={false} />
        )}
        <EventMenu event={event} />
      </Box>
      <CardContent>
        <Grid container spacing={3}>
          <Grid container item direction={"column"}>
            <Grid container item>
              <Typography gutterBottom variant="h6" noWrap overflow={"hidden"}>
                {event.title}
              </Typography>
            </Grid>
            <Grid display="flex" alignItems={"center"} gap={1}>
              {event.draft ? (
                <Chip
                  label={"Draft"}
                  variant="outlined"
                  sx={{ borderRadius: "0", my: 1 }}
                />
              ) : event.publishAt ? (
                isEventScheduled(event.publishAt) ? (
                  <Chip
                    label={"Scheduled"}
                    variant="outlined"
                    sx={{ borderRadius: "0", my: 1 }}
                  />
                ) : (
                  <Chip
                    label={"Published"}
                    color="primary"
                    variant="filled"
                    sx={{ borderRadius: "0", my: 1 }}
                  />
                )
              ) : (
                ""
              )}
              {event.private && (
                <Chip
                  label={"Private"}
                  variant="outlined"
                  sx={{ borderRadius: "0", my: 1 }}
                />
              )}
              {!event.draft &&
                event.publishAt &&
                isEventPublished(event.publishAt) && (
                  <Grid container justifyContent="flex-end">
                    <Grid item direction={"column"}>
                      <Typography variant="body2" sx={{ mb: 0 }}>
                        Event Links
                      </Typography>
                      <CardActions sx={{ py: 0 }}>
                        <Grid container justifyContent={"space-between"}>
                          <Grid item>
                            <ShareColumn
                              emailText={"Join my event on meco:"}
                              url={getPublicEventUrl(
                                landingPageUrl,
                                event.publicId!!
                              )}
                            />
                          </Grid>
                        </Grid>
                      </CardActions>
                    </Grid>
                  </Grid>
                )}
            </Grid>
          </Grid>

          <Grid container item gap={1}>
            <Typography variant="body2">
              <b>Event Summary</b>
            </Typography>
            <Grid
              container
              item
              gap={2}
              alignItems={"center"}
              flexWrap={"nowrap"}
            >
              <LocationOnIcon />
              <Typography
                variant="body2"
                color="text.secondary"
                overflow={"hidden"}
                noWrap
              >
                {event.location?.streetAddress || "-"}
              </Typography>
            </Grid>
            <Grid container item gap={2} alignItems={"center"}>
              <CalendarMonthIcon />
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  {new Date(event.date.fromDate).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="primary">
                  {new Date(event.date.fromDate).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2" color="text.primary">
                  -
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  {new Date(event.date.toDate).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="primary">
                  {new Date(event.date.toDate).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              item
              gap={2}
              alignItems={"center"}
              flexWrap={"nowrap"}
            >
              <TagIcon />
              <Typography
                variant="body2"
                color="text.secondary"
                overflow={"hidden"}
                noWrap
              >
                {event.tags && event.tags.length > 0
                  ? event.tags.map((tag, i) => (
                      <span key={i}>{`${tag}${
                        i !== event.tags!!.length - 1 ? ", " : ""
                      }`}</span>
                    ))
                  : "-"}
              </Typography>
            </Grid>
            <Grid
              container
              item
              gap={2}
              alignItems={"center"}
              flexWrap={"nowrap"}
            >
              <ConfirmationNumberIcon />
              <Typography
                variant="body2"
                color="text.secondary"
                overflow={"hidden"}
                noWrap
              >
                {`${event.ticketTypes.length} Ticket${
                  event.ticketTypes.length !== 1 ? "s" : ""
                }`}
              </Typography>
            </Grid>

            <Typography variant="body1" sx={{ mt: 2 }}>
              <b>Event Code (Staff-App)</b>
            </Typography>
            <Grid container item display="flex" gap={2} alignItems={"center"}>
              {event.code && (
                <>
                  <QrCodeMenu event={event} />
                  <ShareColumn
                    value={event.code}
                    emailText={`Join my event on Meco: ${event.code}`}
                    url={`${staffAppUrl}://event?code=${event.code}`}
                  />
                </>
              )}
            </Grid>

            {!branch && (
              <Grid
                container
                item
                gap={2}
                alignItems={"center"}
                flexWrap={"nowrap"}
              >
                <ApartmentIcon />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  overflow={"hidden"}
                  noWrap
                >
                  {branches.find((b) => b.id === event.branchId)?.name}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Grid container justifyContent={"space-between"}>
          <ShowMenuItem
            resource="events"
            id={event.id}
            label={"Event Statistics"}
            startIcon={<AssessmentOutlinedIcon />}
          />
        </Grid>
        <Grid item>
          <EditButton recordItemId={event.id} />
        </Grid>
      </CardActions>
    </Card>
  );
}

export const EventCardSkeleton = () => {
  return (
    <Card>
      <Box position={"relative"}>
        <Skeleton variant="rectangular" height={200} animation={false} />
      </Box>
      <CardContent>
        <Grid container spacing={3}>
          <Grid container item direction={"column"}>
            <Grid container item>
              <Typography gutterBottom variant="h6" width="100%">
                <Skeleton />
              </Typography>
            </Grid>
          </Grid>
          <Grid container item gap={1}>
            <Typography variant="body2" color="text.secondary" width={"60%"}>
              <Skeleton />
            </Typography>
            <Typography variant="body2" color="text.secondary" width={"60%"}>
              <Skeleton />
            </Typography>
            <Typography variant="body2" color="text.secondary" width={"70%"}>
              <Skeleton />
            </Typography>
            <Typography variant="body2" color="text.secondary" width={"60%"}>
              <Skeleton />
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Grid container justifyContent={"flex-end"}>
          <Typography variant="body2" color="text.secondary" width={"30%"}>
            <Skeleton />
          </Typography>
        </Grid>
      </CardActions>
    </Card>
  );
};
